// JournalTimeline — the Journal's row list: a day-grouped list of what
// happened (sources, materials, packages, conversations), one row per entry
// with its kind icon, title, kind and time; and the per-entity mini-timeline
// (one resource's own history) built on the same rows. The Journal's periods
// (modules/journal) show a day's activity with it, and a selected resource
// row's history.
//
// Pure/presentational over pre-formatted rows (no Date, no locale logic):
// `when`/`whenAbsolute` arrive formatted and the day buckets precomputed. The
// impure leaf is the sticky-header observer.

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactNode,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from "react";
import { buildEntityTimeline } from "../../app/entityTimeline.js";
import type { EntityHistoryMoment } from "../../app/ports/versioning.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Text } from "../Text/Text.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import "./JournalTimeline.css";

// === Freshness — the perishable-information vocabulary =====================
// A NAMED tier, not a raw age. The host computes this from a real timestamp
// (this component never touches Date). The timeline deliberately does not
// escalate chrome per-row on freshness — the recap already leads with the
// coarser "last active" fact, so a second per-row staleness signal would be
// redundant. The tier still marks the SUMMARY, where staleness earns one
// honest sentence naming it.
export type FreshnessTier = "fresh" | "aging" | "stale";

// === Recent activity — pointer rows that LINK to their conversation/file ===
// Each kind is its own variant (never a generic `kind: string` field) so a
// future kind-specific attribute has somewhere to live without widening every
// row. `when` arrives pre-formatted; `freshness` is the tier the host derived
// from the same instant; `at` is the RAW ISO instant behind both ("" when the
// instant is missing/unknown) — kept so a pure view transform can filter by day
// (J22's from/to range) without re-deriving the instant from the formatted
// label. Reused verbatim as the timeline's entry type — the timeline invents no
// parallel row/kind vocabulary. An empty `title` is an untitled resource
// (the port carried none); the view localizes it.

type SourceActivityRow = {
    readonly type: "source";
    readonly id: string;
    readonly title: string;
    readonly when: string;
    readonly whenAbsolute: string;
    readonly at: string;
    readonly freshness: FreshnessTier;
};
type MaterialActivityRow = {
    readonly type: "material";
    readonly id: string;
    readonly title: string;
    readonly when: string;
    readonly whenAbsolute: string;
    readonly at: string;
    readonly freshness: FreshnessTier;
};
type PackageActivityRow = {
    readonly type: "package";
    readonly id: string;
    readonly title: string;
    readonly when: string;
    readonly whenAbsolute: string;
    readonly at: string;
    readonly freshness: FreshnessTier;
};
type ConversationActivityRow = {
    readonly type: "conversation";
    readonly id: string;
    readonly title: string;
    readonly when: string;
    readonly whenAbsolute: string;
    readonly at: string;
    readonly freshness: FreshnessTier;
};
export type ActivityRow =
    | SourceActivityRow
    | MaterialActivityRow
    | PackageActivityRow
    | ConversationActivityRow;

// J30 — how the timeline shows each row's date: a compact RELATIVE label ("3d")
// or the short ABSOLUTE date ("23 sep"). A display preference (view-ephemeral),
// not a sort or filter; both labels are pre-formatted by the host.
export type DateMode = "relative" | "absolute";

// === Timeline grouping — the day/period spine ==============================
// `DayPeriod` is a PURE semantic bucket; the component maps it to a localized
// label (i18n stays in the React tree, the projection is Lingui-free). The
// grouping is by WHEN, never by kind — a group's entries span every entity
// kind, interleaved by recency, because the point is a workspace-wide
// overview (Miller's law: ~8 rows chunked into 2-3 headed buckets rather than
// one flat scan).
export type DayPeriod = "today" | "yesterday" | "earlier";
export type TimelineGroup = {
    readonly period: DayPeriod;
    readonly entries: readonly ActivityRow[];
};

// === Per-kind icon (module-level: no i18n needed) ===========================

const kindIcon = (kind: ActivityRow["type"]): IconName => {
    switch (kind) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "package":
            return "export";
        case "conversation":
            return "chat";
        default:
            return exhaustSwitchCase(kind);
    }
};

// === Per-kind label (a hook so the ONE switch is SHARED between the timeline
// and the activity detail, never duplicated). The noun labels carry the shared
// "resource kind" Lingui context — the same the resource-picker surfaces use —
// so they never collide with the same words used elsewhere as button verbs
// (bare "Package" the action → "Paketera", "Package"/"resource kind" → "Paket").
const useKindLabel = (): ((kind: ActivityRow["type"]) => string) => {
    const { t } = useLingui();
    return (kind) => {
        switch (kind) {
            case "source":
                return t({ message: "Source", context: "resource kind" });
            case "material":
                return t({ message: "Material", context: "resource kind" });
            case "package":
                return t({ message: "Package", context: "resource kind" });
            case "conversation":
                return t({ message: "Conversation", context: "resource kind" });
            default:
                return exhaustSwitchCase(kind);
        }
    };
};

// === Sticky-header rework — one SWAPPING section header ======================
// The J24 multi-sticky approach let two day-labels show at once ("mellanläge")
// and a row could rest half-hidden under a sticky label. Instead ONE sticky
// header renders the CURRENT period, driven by an IntersectionObserver on the
// day-group boundaries (not a scroll listener). Parse-don't-validate the raw
// `data-period` attribute back into the DayPeriod sum. `null` when unknown.
const toDayPeriod = (raw: string | null): DayPeriod | null =>
    raw === "today" || raw === "yesterday" || raw === "earlier" ? raw : null;

// Which day-group currently sits at the top of the scrolling timeline. The
// observer flips the answer completely as one group scrolls off the top and the
// next takes its place, so exactly one header is ever shown (no in-between). The
// impure observer lives in this effect (impure shell — fine); the returned value
// is view-ephemeral (scroll position derived), never projected or a command.
// `periodKey` re-arms the observer whenever the rendered groups change (the date
// filter). `--no-scroll` case: an empty root just leaves the initial period.
const useCurrentPeriod = (
    scrollRef: RefObject<HTMLElement | null>,
    periodKey: string,
): DayPeriod | null => {
    const [current, setCurrent] = useState<DayPeriod | null>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: periodKey is an intended re-arm trigger (the rendered groups changed via the date filter); the group nodes are queried fresh each run.
    useEffect(() => {
        const root = scrollRef.current;
        if (root === null) return;
        const groups = Array.from(
            root.querySelectorAll<HTMLElement>("[data-period]"),
        );
        if (groups.length === 0) {
            setCurrent(null);
            return;
        }
        // Seed with the first group so the header is right before any scroll.
        setCurrent(toDayPeriod(groups[0]?.getAttribute("data-period") ?? null));
        const visible = new Set<Element>();
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) visible.add(entry.target);
                    else visible.delete(entry.target);
                }
                // The current header is the TOPMOST (DOM-order) group still
                // touching the thin band at the top of the scroll box.
                const topmost = groups.find((group) => visible.has(group));
                const period = toDayPeriod(
                    topmost?.getAttribute("data-period") ?? null,
                );
                if (period !== null) setCurrent(period);
            },
            // The active band is a thin strip at the very top: a group counts as
            // "current" only while it intersects the top ~20% of the scroll box.
            { root, rootMargin: "0px 0px -80% 0px", threshold: 0 },
        );
        for (const group of groups) observer.observe(group);
        return () => observer.disconnect();
    }, [scrollRef, periodKey]);
    return current;
};

// === The timeline — a vertical rail, day-grouped, one dot per entry, EVERY
// entity kind interleaved by recency. The intro line states the scope out
// loud: this is the whole workspace, and materials are one first-class row
// kind among several, not a separate sub-history. ============================

export const Timeline = ({
    groups,
    dateMode,
    selected,
    onSelect,
    caption = true,
}: {
    readonly groups: readonly TimelineGroup[];
    readonly dateMode: DateMode;
    // The currently-previewed row (view-ephemeral, owned by the parent), so a
    // row can mark itself selected. Clicking a row SELECTS it (previews it in
    // the right detail pane) — it never navigates; that path is the detail's "Öppna".
    readonly selected: ActivityRow | null;
    readonly onSelect: (row: ActivityRow) => void;
    // false: no visible day captions (the host already heads the list with
    // its day, as the Journal's period does).
    readonly caption?: boolean;
}): ReactNode => {
    const { t } = useLingui();
    const kindLabel = useKindLabel();

    // J31/sticky-header — the scroll container (also the observer root + the
    // keyboard-nav container). View-ephemeral impure-edge concerns: the ref and
    // the imperative focus below live here, not in the projection.
    const listRef = useRef<HTMLElement | null>(null);

    const renderGroups = groups.filter((group) => group.entries.length > 0);
    // The flat, rendered row order (groups already in recency order) — the spine
    // ArrowUp/ArrowDown walks across day-group boundaries.
    const rows = renderGroups.flatMap((group) => group.entries);
    // Re-arm the sticky-header observer whenever the rendered groups change.
    const periodKey = renderGroups.map((group) => group.period).join("|");
    const currentPeriod = useCurrentPeriod(listRef, periodKey);

    const periodLabel = (period: DayPeriod): string => {
        switch (period) {
            case "today":
                return t`Today`;
            case "yesterday":
                return t`Yesterday`;
            case "earlier":
                return t`Earlier`;
            default:
                return exhaustSwitchCase(period);
        }
    };

    // J31 — a stable per-row key stamped on each button, so the keydown handler
    // can find the anchor row (its own DOM node) and move DOM focus to the next.
    const entryKey = (row: ActivityRow): string => `${row.type}-${row.id}`;

    // J31 (roving tabindex) — the SELECTED row is the list's single tab stop, so
    // Tab lands directly on it (no clicking first). When the selection is not
    // among the rendered rows (e.g. filtered out), the container itself becomes
    // focusable instead, so Tab can still enter the list and arrows anchor on the
    // `selected` fallback. Exactly one of {a row, the container} is tabbable.
    const selectedInRows =
        selected !== null &&
        rows.some((row) => entryKey(row) === entryKey(selected));

    // Imperative focus of a row's button by its stable key. querySelectorAll +
    // getAttribute avoids escaping ids into a CSS attribute selector. This is a
    // view-ephemeral focus move (like any list's arrow-nav), not domain state.
    const focusRow = (row: ActivityRow): void => {
        const container = listRef.current;
        if (container === null) return;
        const key = entryKey(row);
        const match = Array.from(
            container.querySelectorAll<HTMLElement>("[data-entry-key]"),
        ).find((el) => el.getAttribute("data-entry-key") === key);
        if (match !== undefined) match.focus();
    };

    // J31 — ArrowUp/ArrowDown (plus Home/End) walk the flat row list, moving BOTH
    // the selection preview and DOM focus. The handler lives on the LIST CONTAINER
    // (keydown bubbles up from whichever row has focus), so it also fires when the
    // container itself is focused — arrows then anchor on the already-`selected`
    // row without a prior click. Standard in-list navigation within a focused list
    // (like the Bibliotek results), NOT a chrome-level chord: it only acts while
    // focus is inside this list and never claims a browser shortcut. No
    // wrap-around: it stops at the ends.
    const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>): void => {
        const { key } = event;
        if (
            key !== "ArrowDown" &&
            key !== "ArrowUp" &&
            key !== "Home" &&
            key !== "End"
        ) {
            return;
        }
        if (rows.length === 0) return;
        const target = event.target;
        const focusedKey =
            target instanceof HTMLElement
                ? (target
                      .closest("[data-entry-key]")
                      ?.getAttribute("data-entry-key") ?? null)
                : null;
        const anchorKey =
            focusedKey ?? (selected !== null ? entryKey(selected) : null);
        const currentIndex =
            anchorKey === null
                ? -1
                : rows.findIndex((row) => entryKey(row) === anchorKey);
        const nextIndex =
            key === "Home"
                ? 0
                : key === "End"
                  ? rows.length - 1
                  : key === "ArrowDown"
                    ? currentIndex < 0
                        ? 0
                        : Math.min(currentIndex + 1, rows.length - 1)
                    : currentIndex < 0
                      ? 0
                      : Math.max(currentIndex - 1, 0);
        const next = rows[nextIndex];
        if (next === undefined) return;
        event.preventDefault();
        onSelect(next);
        focusRow(next);
    };

    // No rows, no list: the host says what an empty history means.
    if (rows.length === 0) return null;
    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: the list is a roving-tabindex nav container — it is focusable (tabIndex below) and its rows are buttons; the keydown is standard in-list arrow navigation, not a static-element click handler.
        <section
            className="who-timeline"
            ref={listRef}
            tabIndex={selectedInRows ? -1 : 0}
            onKeyDown={onKeyDown}
        >
            {/* Sticky-header rework — ONE header showing the CURRENT period, swapped
                completely by the observer (no "mellanläge"). It is opaque + full
                width so rows scroll cleanly UNDER it (no half-hidden sliver). The
                per-group in-flow heading is kept for assistive tech only, so screen
                readers still hear each day boundary. aria-hidden here avoids a
                duplicate announcement. Keyed on the period for a fast subtle swap. */}
            {caption && currentPeriod !== null ? (
                <div className="who-timelineHeader" aria-hidden="true">
                    <Text
                        as="p"
                        size="xs"
                        weight="semibold"
                        tone="faint"
                        className="who-groupLabel who-groupLabel--swap"
                        key={currentPeriod}
                    >
                        {periodLabel(currentPeriod)}
                    </Text>
                </div>
            ) : null}
            {renderGroups.map((group) => {
                // Every day-group shows a VISIBLE in-flow caption, so no group
                // ever lacks a header. The ONE exception is the group whose
                // period currently coincides with the floating header above:
                // its in-flow twin is visually hidden (visibility: hidden keeps
                // the layout box, so rows never jump as the floating header
                // swaps IGÅR→TIDIGARE) to avoid showing the same label twice.
                // The VisuallyHidden sibling always owns the screen-reader
                // announcement; the visible caption is aria-hidden. Before the
                // observer seeds (currentPeriod === null) every caption shows —
                // an honest fallback, never a headerless group.
                const isCurrent = group.period === currentPeriod;
                return (
                    <div
                        className="who-group"
                        data-period={group.period}
                        key={group.period}
                    >
                        {caption ? (
                            <VisuallyHidden as="div">
                                {periodLabel(group.period)}
                            </VisuallyHidden>
                        ) : null}
                        {caption ? (
                            <div
                                className={`who-groupCaption${isCurrent ? " who-groupCaption--currentHidden" : ""}`}
                                aria-hidden="true"
                            >
                                <Text
                                    as="p"
                                    size="xs"
                                    weight="semibold"
                                    tone="faint"
                                    className="who-groupLabel"
                                >
                                    {periodLabel(group.period)}
                                </Text>
                            </div>
                        ) : null}
                        <ol className="who-rail">
                            {group.entries.map((entry) => {
                                const isSelected =
                                    selected?.type === entry.type &&
                                    selected?.id === entry.id;
                                const shownWhen =
                                    dateMode === "absolute"
                                        ? entry.whenAbsolute
                                        : entry.when;
                                return (
                                    <li
                                        className="who-railItem"
                                        key={`${entry.type}-${entry.id}`}
                                    >
                                        <button
                                            type="button"
                                            className={`who-entry${isSelected ? " who-entry--selected" : ""}`}
                                            aria-current={
                                                isSelected ? "true" : undefined
                                            }
                                            tabIndex={isSelected ? 0 : -1}
                                            data-entry-key={`${entry.type}-${entry.id}`}
                                            onClick={() => onSelect(entry)}
                                        >
                                            <Icon
                                                name={kindIcon(entry.type)}
                                                size="sm"
                                                className="who-entryIcon"
                                            />
                                            <Text
                                                as="span"
                                                size="sm"
                                                className="who-entryTitle"
                                            >
                                                {entry.title === ""
                                                    ? t`Untitled`
                                                    : entry.title}
                                            </Text>
                                            <Text
                                                as="span"
                                                size="xs"
                                                tone="faint"
                                                className="who-entryKind"
                                            >
                                                {kindLabel(entry.type)}
                                            </Text>
                                            {shownWhen ? (
                                                <Text
                                                    as="span"
                                                    size="xs"
                                                    tone="faint"
                                                    className="who-entryWhen"
                                                >
                                                    {shownWhen}
                                                </Text>
                                            ) : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                );
            })}
        </section>
    );
};

// === The per-entity mini-timeline ============================================
// The Journal's own day-grouped rows, narrowed to ONE resource's server-folded
// moments via buildEntityTimeline. The history arrives as a projected state
// (loading / loaded / failed, owned by the journal module); every state shows
// the heading, so the place never collapses to a blank. The per-kind titles
// are built here (Lingui) and threaded into the pure projection. Selection is
// a view-ephemeral highlight only, defaulting to the newest moment.

// One resource's history as the host holds it.
export type EntityHistory =
    | { readonly type: "loading" }
    | {
          readonly type: "loaded";
          readonly moments: readonly EntityHistoryMoment[];
      }
    | { readonly type: "failed" };

export const EntityMiniTimeline = ({
    entity,
    history,
    nowMs,
}: {
    readonly entity: ActivityRow;
    readonly history: EntityHistory;
    readonly nowMs: number;
}): ReactNode => {
    const { t, i18n } = useLingui();
    // View-ephemeral highlight only; the effective selection falls back to the
    // newest moment (the mini-timeline is newest-first) so a row reads selected
    // without an effect seeding it post-load.
    const [selected, setSelected] = useState<ActivityRow | null>(null);

    // The section heading names a stable, explicit place — shown in EVERY state
    // (loading / failed / empty / populated), so the mini-journal never collapses
    // to an invisible blank that hides which state it is in. This is the
    // per-entity slice of the client state-machine direction: loading, failure,
    // and emptiness are distinct EXPECTED states, never a silent null.
    const heading = ((kind: ActivityRow["type"]): string => {
        switch (kind) {
            case "source":
                return t`In this source`;
            case "material":
                return t`In this material`;
            case "package":
                return t`In this package`;
            case "conversation":
                return t`In this conversation`;
            default:
                return exhaustSwitchCase(kind);
        }
    })(entity.type);
    const headingEl = (
        <Text
            as="span"
            size="sm"
            weight="semibold"
            tone="dim"
            className="who-colHeading"
        >
            {heading}
        </Text>
    );
    const note = (message: string): ReactNode => (
        <>
            {headingEl}
            <Text as="p" size="sm" tone="faint">
                {message}
            </Text>
        </>
    );

    if (history.type === "loading") return note(t`Loading history…`);
    if (history.type === "failed") return note(t`History could not be loaded.`);

    const momentTitle = (moment: EntityHistoryMoment): string => {
        switch (moment.kind) {
            case "ingested_source":
            case "authored_material":
                return t`Created`;
            case "edited_material": {
                const count = moment.edits ?? 0;
                return t`Edited (${count} changes)`;
            }
            case "packaged_material":
                return t`Packaged`;
            case "restored_resource":
                return t`Restored`;
            case "deleted_resource":
                return t`Removed`;
            case "version_restored":
                return t`Restored to an earlier version`;
        }
    };

    const groups = buildEntityTimeline({
        moments: history.moments,
        nowMs,
        locale: i18n.locale,
        momentTitle,
    });
    // Loaded but empty is a real, expected state (e.g. a package whose packaging
    // moment keys to its material, so its own stream is empty) — say so
    // explicitly, never a blank.
    if (groups.length === 0) return note(t`No activity yet.`);

    const newest = groups[0]?.entries[0] ?? null;
    const effectiveSelected = selected ?? newest;

    return (
        <>
            {headingEl}
            <Timeline
                groups={groups}
                dateMode="relative"
                selected={effectiveSelected}
                onSelect={setSelected}
            />
        </>
    );
};
